/**
 * The trainer's conversations with Buddy as the history rail lists them (board P): what each one
 * waits for, how the list is grouped by age, and how a row's moment is shown.
 *
 * Pure: the clock and the zone arrive as parameters, and nothing here words anything.
 */

/** What a conversation waits for, as the server says it (`waitsFor` on the history's summary). */
export type AiWaitsFor = 'answers' | 'approval' | 'draft' | 'saved' | 'nothing';

export interface AiHistoryEntry {
  readonly id: number;
  /** The conversation's title; `null` while it has none (nothing was asked yet). */
  readonly title: string | null;
  /** The client the plan is built for, or `null` for the trainer's own library. */
  readonly clientId: number | null;
  readonly waitsFor: AiWaitsFor;
  /** Buddy is working on a turn of this conversation right now. */
  readonly working: boolean;
  /** How many finished drafts the conversation holds; worded only when there are several. */
  readonly draftVersions: number;
  /** When the conversation last moved, ISO 8601. */
  readonly lastMessageAt: string;
}

/** The wire's facts a row is read from; the generated summary DTO is assignable to it. */
export interface AiHistoryWireEntry {
  readonly id: number;
  readonly title?: string | null;
  readonly customerId?: number | null;
  readonly waitsFor?: string | null;
  readonly draftVersions?: number;
  readonly activeTurnId?: number | null;
  readonly lastMessageAt: string;
}

/** The server's own word for what a conversation waits for; one this build does not know is nothing. */
function waitsFor(wire: string | null | undefined): AiWaitsFor {
  switch (wire) {
    case 'questions':
      return 'answers';
    case 'outline':
      return 'approval';
    case 'draft':
      return 'draft';
    case 'savedPlan':
      return 'saved';
    default:
      return 'nothing';
  }
}

export function historyEntryFromWire(entry: AiHistoryWireEntry): AiHistoryEntry {
  const title = entry.title?.trim() ?? '';
  return {
    id: entry.id,
    title: title === '' ? null : title,
    clientId: typeof entry.customerId === 'number' && entry.customerId > 0 ? entry.customerId : null,
    waitsFor: waitsFor(entry.waitsFor),
    working: typeof entry.activeTurnId === 'number',
    draftVersions: Math.max(0, entry.draftVersions ?? 0),
    lastMessageAt: entry.lastMessageAt,
  };
}

export type AiHistoryGroupKey = 'today' | 'week' | 'older';

export interface AiHistoryGroup {
  readonly key: AiHistoryGroupKey;
  readonly entries: readonly AiHistoryEntry[];
}

/** A calendar day in `timeZone`, as a count of days: two instants on one local day compare equal. */
function localDay(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(instant)
    .reduce<Record<string, number>>((read, part) => ({ ...read, [part.type]: Number(part.value) }), {});
  return Math.floor(Date.UTC(parts.year ?? 0, (parts.month ?? 1) - 1, parts.day ?? 1) / 86_400_000);
}

/** How many of the trainer's calendar days ago a conversation last moved; never negative. */
function daysAgo(iso: string, now: Date, timeZone: string): number {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return Number.POSITIVE_INFINITY;
  return Math.max(0, localDay(now, timeZone) - localDay(at, timeZone));
}

/**
 * The rail's groups, newest first inside each: „Dziś" (the trainer's own calendar day), „Ostatnie
 * 7 dni" (the six days before it) and „Starsze". A group with nothing in it is not listed, and a
 * moment that cannot be read is the oldest thing there is rather than today.
 */
export function historyGroups(entries: readonly AiHistoryEntry[], now: Date, timeZone: string): AiHistoryGroup[] {
  const newestFirst = entries.slice().sort((a, b) => Date.parse(b.lastMessageAt) - Date.parse(a.lastMessageAt));
  const groups: Record<AiHistoryGroupKey, AiHistoryEntry[]> = { today: [], week: [], older: [] };
  for (const entry of newestFirst) {
    const age = daysAgo(entry.lastMessageAt, now, timeZone);
    groups[age === 0 ? 'today' : age <= 6 ? 'week' : 'older'].push(entry);
  }
  return (['today', 'week', 'older'] as const)
    .filter((key) => groups[key].length > 0)
    .map((key) => ({ key, entries: groups[key] }));
}

/**
 * How a row's moment is shown (board P): the clock time today, the weekday within the last week,
 * the date beyond it. The same boundaries as the groups, so a row never reads against its heading.
 */
export type AiHistoryMoment = 'time' | 'weekday' | 'date';

export function historyMoment(iso: string, now: Date, timeZone: string): AiHistoryMoment {
  const age = daysAgo(iso, now, timeZone);
  return age === 0 ? 'time' : age <= 6 ? 'weekday' : 'date';
}
